/**
 * Finance Reports Service — BE-14a finance, BE-14d-1 sales, BE-14d-2 operations reports (`/api/reports/...`) and the
 * BE-15b health check.
 * Real backend. Returns camelCase data. Dates are YYYY-MM-DD, inclusive; a TC user always gets its own TC.
 * Export: export.service.js (BE-14b).
 */

import ApiService from '../api.service';
import { unwrapData, unwrapList } from '../../utils/apiConvert';

const clean = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== '' && v !== undefined && v !== null));
const get = async (path, params) => unwrapData(await ApiService.get(`/api/reports/${path}`, { params: clean(params) }));

export const financeReportsService = {
  // { from, to, customerId } → received (the headline) and settled (wallet applied to invoices, information only)
  getCollections: (params) => get('collections', params),

  // { customerId } → as of today: credit (wallet), debit (owed), net position per customer
  getCustomerAdvancesDebtors: (params) => get('customer-advances-debtors', params),

  // { from, to, customerId, status } → one row per receipt; status PAID / PARTIAL / VOIDED
  getInvoiceReceipts: (params) => get('invoice-receipts', params),

  // { bankAccountId (required), from, to, customerId } → money in / out with the running balance
  getBankStatement: (params) => get('bank-statement', params),

  // { from, to } → operating / investing / financing lines with totals, net change, opening and closing cash
  getCashFlow: (params) => get('cash-flow', params),

  // live: bank / cash / wallet balances, total cash, card balances owed, issued cheques not cleared
  getCashPosition: () => get('cash-position'),

  // as of today: per customer current, 1-30, 31-60, 61-90, over 90 and total, plus totals
  getReceivablesAging: () => get('receivables-aging'),

  // direction 'output' | 'input', { from, to } → rows by tax point date with base, VAT, total
  getTaxReport: (direction, params) => get(`tax-${direction}`, params),

  // BE-14d-1 sales reports: FINAL invoices finalised from..to (the day the ledger posted them)
  // { from, to, customerId, status } → rows per invoice with revenue / cost / profit, paid and due
  getInvoiceReport: (params) => get('invoice-report', params),

  // { from, to, sort: PROFIT | VOLUME } → rows per service (Profit Report / Service Mix) and totals
  getServiceProfit: (params) => get('service-profit', params),

  // { from, to } → rows per assigned agent (an "Unassigned" row last) and totals
  getAgentPerformance: (params) => get('agent-performance', params),

  // { from, to, type } → per recipient: earned / paid in the range, outstanding / payable / receivable now
  getStaffCommission: (params) => get('staff-commission', params),

  // { from, to, userId } (dates taken by month) -> people with month rows, byMonth, missing months, total, unpaidPayables
  getSalaryReport: (params) => get('salary', params),

  // BE-14d-2 operations reports
  // { from, to } → providers paid (with the paid-from split) and expense vendors billed / paid, outstanding now
  getSuppliers: (params) => get('suppliers', params),

  // { date } → that day's entries with the opening / in / out / closing cash; posted_at is an ISO time with offset
  getDailySheet: (params) => get('daily-sheet', params),

  // { from, to } (max 31 days) → one row per day: date, cashIn, cashOut, net — the cash accounts the daily sheet uses
  getDailyCashRange: (params) => get('daily-sheet/range', params),

  // { days } → customers with no invoice, work order or top-up for `days` or more (or never)
  getInactiveClients: (params) => get('inactive-clients', params),

  // { year, basis: ACCRUAL | CASH } → P&L sections with 12 month columns and the year
  getAnnualPl: (params) => get('annual-pl', params),

  // { from, to, categoryId, expenseType: PAID | PAYABLE | RETURN } → expenses grouped by category / vendor / method + the list
  getExpenseReport: (params) => get('expenses', params),

  // BE-15b: ten checks of the books against their records (400 until the ledger has started)
  getHealthCheck: async () => unwrapData(await ApiService.get('/api/accounting/health-check')),

  // The TC's customers for the pickers: [{ id, label }]
  getCustomers: async (typingCenterId) => {
    const res = await ApiService.getInternalOrganizations(typingCenterId);
    return (res?.data?.data || [])
      .map((o) => ({ id: o.id, label: o.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  },

  // Bank, cash and e-wallet accounts for the bank report: [{ id, label }]
  getMoneyAccounts: async () => {
    const { items } = unwrapList(await ApiService.getTcBankAccounts());
    return items
      .filter((a) => ['OPERATIONS', 'CASH', 'E_WALLET', 'SETTLEMENT'].includes(a.accountType) && !a.deletedAt)
      .map((a) => ({ id: a.id, label: [a.bankName, a.accountName].filter(Boolean).join(' - '), active: a.isActive !== false }));
  },
};

export default financeReportsService;
